<template>
    <b-modal static v-model="loginPopup" id="login-popup" dialog-class="login-dialogClass" hide-footer size="md" :title="title" centered>
        <div class="center">
            <div v-if="!showGetApp">
                <!-- Firebase Auth container -->
                <section v-if="!isEmbeddedBrowser" class="firebaseui-auth-container"></section>

                <div v-if="!isEmbeddedBrowser && rsvpFlow" class="my-3">or</div>
                <!-- Name + Email RSVP -->
                <div v-if="rsvpFlow && smsRsvp" class="rounded">
                    <b-tabs content-class="mt-3" align="center">
                        <b-tab title="Phone" active>
                            <MazPhoneNumberInput :countrySelectorWidth="'100%'" class="mb-3" size="sm" show-code-on-list @update="handlePhoneData" />
                            <b-form-input v-if="!isPrivate" id="input-name" v-model="name" :state="nameState" placeholder="Name"></b-form-input>

                            <p class="mt-3 footnote">You will receive notifications about this event via SMS.</p>

                            <b-button class="w-100" :variant="rsvpDisabledPhone ? 'secondary' : 'success'" :disabled="rsvpDisabledPhone" v-on:click="confirmRsvp()">Confirm RSVP</b-button>
                        </b-tab>

                        <b-tab title="Email">
                            <b-form-input id="input-email" v-model="email" @keydown.space.prevent :state="emailState" placeholder="Email" class="mb-3"></b-form-input>
                            <b-form-input v-if="!isPrivate" id="input-name" v-model="name" :state="nameState" placeholder="Name"></b-form-input>

                            <div class="form-check mt-3">
                                <b-form-checkbox v-model="notificationsEnabled" class="form-check-input">Receive email notifications about this event</b-form-checkbox>
                            </div>

                            <b-button class="mt-3 w-100" :variant="rsvpDisabledEmail ? 'secondary' : 'success'" :disabled="rsvpDisabledEmail" v-on:click="confirmRsvp()">Confirm RSVP</b-button>
                        </b-tab>
                    </b-tabs>
                </div>
                <div v-if="rsvpFlow && !smsRsvp" class="rounded">
                    <b-form-input id="input-email" v-model="email" @keydown.space.prevent :state="emailState" placeholder="Email" class="mb-3"></b-form-input>
                    <b-form-input v-if="!isPrivate" id="input-name" v-model="name" :state="nameState" placeholder="Name"></b-form-input>

                    <div class="form-check mt-3">
                        <b-form-checkbox v-model="notificationsEnabled" class="form-check-input">Receive email notifications from dateit</b-form-checkbox>
                    </div>

                    <b-button class="mt-3 w-100" :variant="rsvpDisabledEmail ? 'secondary' : 'success'" :disabled="rsvpDisabledEmail" v-on:click="confirmRsvp()">Confirm RSVP</b-button>
                </div>
            </div>

            <div v-if="showGetApp">
                <div class="vstack gap-3">
                    <font-awesome-icon icon="fa-solid fa-circle-check" class="text-success check-badge-size mx-auto" />
                    <h4 class="mb-2">Confirmed!</h4>
                    <div v-if="!disableAds" class="vstack gap-3">
                        <app-banner></app-banner>
                        <Adsense data-ad-client="ca-pub-8295272717426403" data-ad-slot="8642576966"> </Adsense>
                        <amazon-link :event="viewModel.event"></amazon-link>
                    </div>
                    <div v-else>
                        <app-banner></app-banner>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import "firebaseui/dist/firebaseui.css";
import { nextTick } from "vue";
import EventViewModel from "@/EventViewModel";
import { Attendance } from "@/data/Responses";
import { inject } from "vue";
import Api from "@/Api";
import AuthManager from "@/AuthManager";
import { useToast } from "vue-toastification";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";
import { mobileUtil } from "@/util/MobileUtil";

export default defineComponent({
    name: "LoginPopup",
    mixins: [],
    components: {
        FontAwesomeIcon,
        MazPhoneNumberInput,
    },
    props: {},
    setup() {
        const api = inject<Api>("api");
        if (!api) {
            throw new Error("API not provided");
        }
        const authManager = inject<AuthManager>("authManager");
        if (!authManager) {
            throw new Error("API not provided");
        }
        const toast = useToast();
        const loginPopup = ref(false);

        const { isEmbeddedBrowser, appLink } = mobileUtil();
        return {
            api,
            authManager,
            toast,
            loginPopup,
            isEmbeddedBrowser,
            appLink,
        };
    },
    data() {
        return {
            rsvpFlow: false,
            title: "Login",
            visible: false,
            path: window.location.href,
            name: "",
            email: "",
            phone: "",
            phoneValid: false,
            viewModel: {} as EventViewModel,
            rsvp: Attendance.unknown,
            plusOnes: 0,
            isPrivate: false,
            showGetApp: false,
            useAuth: true,
            notificationsEnabled: true,
        };
    },
    mounted() {},
    computed: {
        disableAds(): boolean {
            return this.viewModel.disableAds;
        },
        smsRsvp(): boolean {
            const isUsa = navigator.language == "en-US";
            return (this.viewModel.event?.features.smsRsvp || false) && isUsa;
        },
        nameState(): boolean | null {
            if (this.name.length > 2) {
                return true;
            }
            return null;
        },
        emailState() {
            var parts = this.email.split("@");
            if (parts.length < 2) {
                return null;
            }
            if (parts[0].length <= 0 || parts[1].length <= 0) {
                return null;
            }

            var end = parts[1].split(".");
            if (end.length < 2) {
                return null;
            }
            if (end[0].length > 0 && end[1].length > 0) {
                return true;
            }
            return null;
        },
        rsvpDisabledPhone(): boolean {
            if (this.isPrivate) {
                return !this.phoneValid;
            }
            return !(this.nameState && this.phoneValid);
        },
        rsvpDisabledEmail(): boolean {
            if (this.isPrivate) {
                return !this.emailState;
            }
            return !(this.nameState && this.emailState);
        },
    },
    methods: {
        showPrivateRsvpLoginFlow(eventViewModel: EventViewModel) {
            this.viewModel = eventViewModel;
            if (document.getElementById("login-popup")?.style.display != "none") {
                return;
            }
            this.rsvpFlow = true;
            this.isPrivate = true;
            this.useAuth = true;
            this.showGetApp = false;
            this.title = "Confirm your RSVP";
            this.presentUi();
        },
        showRsvpLoginFlow(eventViewModel: EventViewModel, rsvp: Attendance, plusOnes: number) {
            this.viewModel = eventViewModel;
            if (document.getElementById("login-popup")?.style.display != "none") {
                return;
            }
            this.rsvp = rsvp;
            this.plusOnes = plusOnes;
            this.rsvpFlow = true;
            this.useAuth = true;
            this.showGetApp = false;
            this.title = "Confirm your RSVP";
            this.presentUi();
        },
        showConfirmRsvpOnlyView(eventViewModel: EventViewModel) {
            this.viewModel = eventViewModel;
            this.useAuth = false;
            this.finishRsvp();
            this.presentUi();
        },
        showLoginUi() {
            if (document.getElementById("login-popup")?.style.display != "none") {
                return;
            }
            this.rsvpFlow = false;
            this.useAuth = true;
            this.showGetApp = false;
            this.title = "Login";
            this.presentUi();
        },
        async presentUi() {
            this.visible = true;
            this.loginPopup = true;
            var self = this;
            this.authManager.setAuthPending(!this.isEmbeddedBrowser);
            // Can't show auth container in embedded browser.
            if (this.isEmbeddedBrowser || !this.useAuth) {
                return;
            }
            // Confirm if this removes the error when looking for the firebase UI
            await nextTick();
            this.authManager.startFirebaseLogin(".firebaseui-auth-container", () => {
                self.hideModal();
            });
        },
        hideModal() {
            this.visible = false;
            this.loginPopup = false;
        },
        async confirmRsvp() {
            if (this.phone != undefined && this.phone != "") {
                if (this.isPrivate) {
                    if (this.viewModel.accessCode == null) {
                        return;
                    }
                    try {
                        await this.api.linkPhone(this.viewModel.eventId, this.phone, this.viewModel.accessCode);
                    } catch (error: any) {
                        if (error.response) {
                            this.toast.error(error.response.data.reason);
                        }
                    }
                    this.finishRsvp();
                    return;
                }
                await this.viewModel.attendWithPhone(this.name, this.phone, this.rsvp, this.plusOnes);
            } else {
                if (this.isPrivate) {
                    if (this.viewModel.accessCode == null) {
                        return;
                    }
                    try {
                        await this.api.linkEmail(this.viewModel.eventId, this.email, this.viewModel.accessCode, this.notificationsEnabled);
                    } catch (error: any) {
                        if (error.response) {
                            this.toast.error(error.response.data.reason);
                        }
                    }
                    this.finishRsvp();
                    return;
                }
                await this.viewModel.attendWithEmail(this.name, this.email, this.rsvp, this.plusOnes, this.notificationsEnabled);
            }

            this.finishRsvp();
        },
        finishRsvp() {
            if (this.rsvp == Attendance.no) {
                this.hideModal();
            } else {
                this.showGetApp = true;
            }
        },
        handlePhoneData(results: any) {
            this.phoneValid = results.isValid;
            this.phone = results.e164;
        },
    },
});
</script>

<style scoped>
:deep(.modal-style) {
    border: none;
    border-radius: 20px;
    padding: 10px;
}

:deep(.modal-header) {
    border: none;
    margin-right: 8px;
    margin-left: 8px;
    padding: 0;
}

:deep(.form-control) {
    background-color: var(--input-bg);
    color: var(--text-primary);
    border-color: var(--input-border);
}

:deep(.form-control:focus) {
    background-color: var(--input-bg);
    color: var(--text-primary);
    border-color: var(--input-border);
}

:deep(.form-control::placeholder) {
    color: var(--text-secondary);
}

:deep(.modal-content) {
    background-color: var(--bg-solid);
    color: var(--text-primary);
}

:deep(.modal-title) {
    color: var(--text-primary);
}

:deep(.nav-tabs .nav-link) {
    color: var(--text-secondary);
}

:deep(.nav-tabs .nav-link.active) {
    color: var(--text-primary);
    background-color: var(--bg-solid);
    border-color: var(--input-border);
}

:deep(.nav-tabs) {
    border-bottom-color: var(--input-border);
}

.center {
    text-align: center;
}

.fillwidth {
    width: 80%;
}

.confirm-box {
    background-color: var(--bg-secondary);
    margin-top: 10px;
}

.footnote {
    font-size: 9pt;
    color: var(--text-secondary);
}

.check-badge-size {
    width: 80px;
    height: 80px;
}

:deep(.form-check) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding-left: 0;
}

:deep(.form-check-input) {
    margin: 0;
    float: none;
}

:deep(.form-check-label) {
    margin: 0;
    padding-left: 0;
}
</style>
